import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Load sidebar state from localStorage with default based on screen size
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) {
      return saved === 'true';
    }
    // Default to false on mobile, true on desktop
    return typeof window !== 'undefined' && window.innerWidth > 1023;
  });
  const [tocOpen, setTocOpen] = useState(() => {
    const saved = localStorage.getItem('tocOpen');
    return saved === 'true';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 767);
      setIsTablet(width <= 1023);
      
      // Auto-close sidebar on mobile when screen becomes small
      if (width <= 1023 && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [sidebarOpen]);

  // Persist sidebar state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebarOpen', String(sidebarOpen));
    }
  }, [sidebarOpen]);

  // Persist TOC state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('tocOpen', String(tocOpen));
    }
  }, [tocOpen]);

  // Memoized callbacks for better performance
  const toggleToc = useCallback(() => setTocOpen(prev => !prev), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const closeToc = useCallback(() => setTocOpen(false), []);

  // Memoized value object to prevent unnecessary re-renders
  const value = useMemo(() => ({
    sidebarOpen,
    setSidebarOpen,
    closeSidebar,
    tocOpen,
    setTocOpen,
    toggleToc,
    closeToc,
    searchQuery,
    setSearchQuery,
    activeSection,
    setActiveSection,
    isMobile,
    isTablet,
  }), [
    sidebarOpen,
    tocOpen,
    searchQuery,
    activeSection,
    isMobile,
    isTablet,
    toggleToc,
    closeSidebar,
    closeToc
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
